import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Dialog } from '@headlessui/react';
import { BellIcon, XMarkIcon, CheckIcon, ArrowPathIcon, PhoneIcon, CubeIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStudioUser } from '../hooks/useStudioUser';
import { Notification } from '../types/supabase';
import { formatLastActivity } from '../utils/activityTracker';
import NotificationComponent from './Notification';

const NotificationsUIContext = createContext<{
  unreadCount: number;
  openNotifications: () => void;
} | null>(null);

export function NotificationsNavButton({
  className = "",
  onNavigate,
}: {
  className?: string;
  onNavigate?: () => void;
}) {
  const ctx = useContext(NotificationsUIContext);
  const unreadCount = ctx?.unreadCount ?? 0;
  const hasUnread = unreadCount > 0;

  return (
    <button
      type="button"
      onClick={() => {
        ctx?.openNotifications();
        onNavigate?.();
      }}
      className={`group flex w-full min-w-0 items-center rounded-md px-2 py-1.5 text-sm font-medium transition ${
        hasUnread
          ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
          : "text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 dark:text-gray-300 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400"
      } ${className}`}
    >
      <BellIcon
        className={`mr-2 h-4 w-4 flex-shrink-0 ${
          hasUnread
            ? "text-indigo-600 dark:text-indigo-400"
            : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
        }`}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 truncate text-left">Notifications</span>
      {hasUnread && (
        <span className="ml-1 flex h-5 min-w-[1.25rem] flex-none items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[11px] font-semibold text-white">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
}

export default function NotificationPopup({
  children,
}: {
  children?: ReactNode;
}) {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [hasCheckedExpiring, setHasCheckedExpiring] = useState(false);
  const [smsNotification, setSmsNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });
  
  // Onboarding checklist state
  const [checklistItems, setChecklistItems] = useState<Record<string, boolean>>({});
  const [checklistLoading, setChecklistLoading] = useState(true);
  const [isChecklistDismissed, setIsChecklistDismissed] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);

  // Multi-select for bulk dismiss
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (studioUser?.studio_id) {
      // Check for expiring payments once per page load
      if (!hasCheckedExpiring) {
        checkExpiringPayments();
        setHasCheckedExpiring(true);
      }
      
      fetchNotifications();
    }
  }, [studioUser?.studio_id]);
  
  // Load checklist dismissal state and checked items - run first
  useEffect(() => {
    const dismissed = localStorage.getItem("onboardingChecklistDismissed");
    if (dismissed === "true") {
      setIsChecklistDismissed(true);
    }
    
    // Load previously saved checked items
    const savedChecked = localStorage.getItem("onboardingChecklistItems");
    if (savedChecked) {
      try {
        const parsed = JSON.parse(savedChecked);
        setChecklistItems(parsed);
      } catch (e) {
        // Invalid JSON, start fresh
        setChecklistItems({});
      }
    } else {
      setChecklistItems({});
    }
  }, []);
  
  // Initialize checklist when studio is available
  useEffect(() => {
    if (studioUser?.studio_id && !isChecklistDismissed) {
      checkOnboardingChecklist();
    }
  }, [studioUser?.studio_id, isChecklistDismissed]);

  const checkExpiringPayments = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      // Check for payments expiring in the next 7 days
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/studio/${studioUser.studio_id}/check-expiring-payments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ daysAhead: 7 }),
        }
      );
      
      // Don't need to handle response - notifications will be fetched next
    } catch (error) {
      console.error('Error checking expiring payments:', error);
    }
  };
  
  const checkOnboardingChecklist = async () => {
    if (!studioUser?.studio_id || isChecklistDismissed) {
      setChecklistLoading(false);
      return;
    }
    
    // Don't auto-check items - let users check them manually
    // Just ensure we have the checklist items structure
    setChecklistLoading(false);
  };
  
  const handleChecklistItemToggle = (itemId: string) => {
    const newCheckedItems = {
      ...checklistItems,
      [itemId]: !checklistItems[itemId],
    };
    setChecklistItems(newCheckedItems);
    localStorage.setItem("onboardingChecklistItems", JSON.stringify(newCheckedItems));
  };
  
  const handleChecklistDismiss = () => {
    setShowDismissConfirm(true);
  };
  
  const confirmDismissChecklist = () => {
    setIsChecklistDismissed(true);
    localStorage.setItem("onboardingChecklistDismissed", "true");
    setShowDismissConfirm(false);
  };
  
  const cancelDismissChecklist = () => {
    setShowDismissConfirm(false);
  };


  const fetchNotifications = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/studio/${studioUser.studio_id}?limit=20`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Consolidate duplicate missed appointment notifications
        const consolidatedNotifications: Notification[] = [];
        const missedAppointmentMap = new Map<string, {notification: Notification, duplicateIds: string[]}>();
        
        for (const notification of data) {
          if (notification.type === 'missed_appointment') {
            // Check if we already have a notification for this studio
            const key = `${notification.studio_id}_missed_appointment`;
            const existing = missedAppointmentMap.get(key);
            
            if (existing) {
              // Track duplicate notification ID to delete later
              existing.duplicateIds.push(notification.id);
              
              // Merge appointments from duplicate notifications
              const existingAppointments = existing.notification.data?.appointments || [];
              const newAppointments = notification.data?.appointments || [];
              
              // Combine appointments, avoiding duplicates by appointment_id
              const allAppointments = [...existingAppointments];
              for (const apt of newAppointments) {
                if (!allAppointments.some((a: any) => a.appointment_id === apt.appointment_id)) {
                  allAppointments.push(apt);
                }
              }
              
              existing.notification.data = {
                ...existing.notification.data,
                appointments: allAppointments
              };
              
              // Update message to reflect total count
              existing.notification.message = allAppointments.length === 1
                ? `Did ${allAppointments[0].lead_name} show up for their scheduled trial class?`
                : `Did ${allAppointments.length} leads show up for their scheduled trial classes?`;
              
              existing.notification.title = allAppointments.length === 1
                ? "Missed Trial Class Appointment"
                : `${allAppointments.length} Missed Trial Class Appointments`;
            } else {
              missedAppointmentMap.set(key, { notification: { ...notification }, duplicateIds: [] });
            }
          } else {
            // Non-missed-appointment notifications pass through
            consolidatedNotifications.push(notification);
          }
        }
        
        // Add consolidated missed appointment notifications and delete duplicates
        missedAppointmentMap.forEach((value) => {
          // Only add if notification is not read and has unresponded appointments
          const appointments = value.notification.data?.appointments || [];
          const hasUnresponded = appointments.some((apt: any) => !apt.responded);
          
          // Only show if not read and has unresponded appointments
          if (!value.notification.is_read && hasUnresponded) {
            consolidatedNotifications.push(value.notification);
          }
          
          // Delete duplicate notifications from the backend
          if (value.duplicateIds.length > 0) {
            value.duplicateIds.forEach((duplicateId) => {
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${duplicateId}`,
                {
                  method: 'DELETE',
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                  },
                }
              ).catch(error => console.error('Error deleting duplicate notification:', error));
            });
          }
        });
        
        setNotifications(consolidatedNotifications);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch notifications:', response.status, response.statusText, errorData);
        
        // If it's an authentication error, show a user-friendly message
        if (response.status === 401) {
          console.warn('Authentication failed - user may need to log in again');
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true }
              : notification
          )
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/studio/${studioUser.studio_id}/read-all`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, is_read: true }))
        );
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const removeAllNotifications = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/studio/${studioUser.studio_id}/all`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        setNotifications([]);
        setSelectedNotificationIds(new Set());
      }
    } catch (error) {
      console.error('Error removing all notifications:', error);
    }
  };

  const toggleSelectNotification = (id: string) => {
    setSelectedNotificationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllNotifications = () => {
    setSelectedNotificationIds(new Set(notifications.map((n) => n.id)));
  };

  const deselectAllNotifications = () => {
    setSelectedNotificationIds(new Set());
  };

  const dismissSelectedNotifications = async () => {
    const ids = Array.from(selectedNotificationIds);
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const authToken = getAuthToken();
    if (authToken) {
      try {
        await Promise.all(
          ids.map((id) =>
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${authToken}` },
            })
          )
        );
      } catch (error) {
        console.error('Error dismissing selected notifications:', error);
      }
    }
    setNotifications((prev) => prev.filter((n) => !idSet.has(n.id)));
    setSelectedNotificationIds(new Set());
  };

  const refreshNotifications = async () => {
    setLoading(true);
    // Don't re-check expiring payments on manual refresh
    // (user may have just deleted the notification)
    await fetchNotifications();
  };

  const handleMissedAppointmentResponse = async (
    notificationId: string,
    appointmentId: string,
    leadId: string,
    showedUp: boolean,
    sendFollowUpSms: boolean = false,
    suppressNotification: boolean = false
  ) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/missed-appointment/${notificationId}/respond`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            appointment_id: appointmentId,
            lead_id: leadId,
            showed_up: showedUp,
            send_follow_up_sms: sendFollowUpSms,
          }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        
        // Show success notification if SMS was sent (unless suppressed for bulk actions)
        if (sendFollowUpSms && !suppressNotification) {
          // Get lead name for the success message - need to get it before refresh
          const currentNotification = notifications.find(n => n.id === notificationId);
          const appointment = currentNotification
            ?.data?.appointments?.find((apt: any) => apt.appointment_id === appointmentId);
          
          const leadName = appointment?.lead_name || 'Lead';
          
          // Set the success notification
          setSmsNotification({
            show: true,
            message: `Follow-up SMS sent to ${leadName}`,
            title: "SMS Sent",
          });
        }
        
        // Refresh notifications to update the UI (after a small delay to show notification)
        setTimeout(async () => {
          await fetchNotifications();
        }, 100);
      } else {
        console.error('Error responding to missed appointment');
      }
    } catch (error) {
      console.error('Error responding to missed appointment:', error);
    }
  };

  const handleBulkMissedAppointmentResponse = async (
    notificationId: string,
    appointments: any[],
    showedUp: boolean
  ) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // If "No" was clicked, automatically send SMS to all with phone numbers
      const sendSms = !showedUp;
      let smsCount = 0;
      const leadNames: string[] = [];

      // Process each appointment (suppress individual notifications for bulk actions)
      for (const appointment of appointments) {
        const willSendSms = sendSms && appointment.lead_phone;
        await handleMissedAppointmentResponse(
          notificationId,
          appointment.appointment_id,
          appointment.lead_id,
          showedUp,
          willSendSms,
          true // Suppress individual notifications for bulk actions
        );
        
        if (willSendSms) {
          smsCount++;
        }
      }
      
      // Show success notification for bulk SMS with count
      if (smsCount > 0) {
        const message = `Follow-up SMS sent to ${smsCount} lead${smsCount > 1 ? 's' : ''}`;
        setSmsNotification({
          show: true,
          message: message,
          title: "SMS Sent",
        });
      }
    } catch (error) {
      console.error('Error in bulk missed appointment response:', error);
    }
  };

  // Add state for dynamic timestamp updates
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every minute for dynamic timestamps
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Function to format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Format as XXX-XXX-XXXX
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // If not 10 digits, return as-is
    return phone;
  };

  // Function to format notification message dates and times
  const formatNotificationMessage = (message: string) => {
    // Pattern to match dates like "2025-09-26 at 19:25:00"
    const dateTimePattern = /(\d{4}-\d{2}-\d{2}) at (\d{2}:\d{2}:\d{2})/g;
    
    return message.replace(dateTimePattern, (match, dateStr, timeStr) => {
      try {
        // Parse the date (YYYY-MM-DD format)
        const [year, month, day] = dateStr.split('-').map(Number);
        
        // Format date as MM/DD/YYYY
        const formattedDate = `${(month).toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        
        // Parse the time (HH:MM:SS format)
        const [hours, minutes] = timeStr.split(':').map(Number);
        
        // Convert to 12-hour format with AM/PM
        const period = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const formattedTime = `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
        
        return `${formattedDate} at ${formattedTime}`;
      } catch (error) {
        // If parsing fails, return original match
        return match;
      }
    });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  
  // Onboarding checklist items
  const onboardingChecklistItems = [
    { id: "gmail", label: "Connect Gmail", link: "/app/settings/integrations" },
    { id: "belts", label: "Setup Belts", link: "/app/settings/general" },
    { id: "classes", label: "Setup Classes", link: "/app/settings/general" },
    { id: "closureDates", label: "Add Studio Closure Dates", link: "/app/settings/general" },
    { id: "memberships", label: "Setup Memberships", link: "/app/settings/general" },
    { id: "staffRoles", label: "Setup Staff Roles", link: "/app/settings/general" },
    { id: "students", label: "Add/Import Students", link: "/app/students" },
    { id: "leads", label: "Add/Import Leads", link: "/app/leads" },
    { id: "staff", label: "Add Staff", link: "/app/staff" },
  ];
  
  const checklistCompletedCount = onboardingChecklistItems.filter(
    (item) => checklistItems[item.id]
  ).length;
  const checklistTotalCount = onboardingChecklistItems.length;
  const allChecklistCompleted = checklistCompletedCount === checklistTotalCount;

  return (
    <NotificationsUIContext.Provider
      value={{
        unreadCount,
        openNotifications: () => setIsOpen(true),
      }}
    >
      {children}
      {/* SMS Success Notification */}
      <NotificationComponent
        show={smsNotification.show}
        message={smsNotification.message}
        title={smsNotification.title}
        type="success"
        onClose={() =>
          setSmsNotification((prev) => ({
            ...prev,
            show: false,
          }))
        }
      />

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-[10015]"
      >
        <div
          className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
          aria-hidden="true"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="flex h-full items-center justify-center p-4 sm:p-6">
            <Dialog.Panel className="relative flex max-h-[calc(100dvh-3rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
          {/* Header - flex-shrink-0 so it stays visible */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                Notifications
              </Dialog.Title>
              <div className="flex items-center space-x-2">
                <button
                  onClick={refreshNotifications}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Refresh notifications"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Close notifications"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={selectAllNotifications}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  >
                    Select all
                  </button>
                  <button
                    onClick={deselectAllNotifications}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  >
                    Deselect all
                  </button>
                  {selectedNotificationIds.size > 0 && (
                    <button
                      onClick={dismissSelectedNotifications}
                      className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    >
                      Dismiss selected ({selectedNotificationIds.size})
                    </button>
                  )}
                  <button
                    onClick={removeAllNotifications}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    Remove all
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Onboarding Checklist Section */}
          {!isChecklistDismissed && (
            <div className="border-b border-gray-200 dark:border-gray-700 bg-indigo-50 dark:bg-indigo-900/20">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Welcome to DojoTrack!
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {checklistCompletedCount} of {checklistTotalCount} completed
                    </p>
                  </div>
                  <button
                    onClick={handleChecklistDismiss}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Dismiss checklist"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                {checklistLoading ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {onboardingChecklistItems.map((item) => {
                      const isChecked = checklistItems[item.id] || false;
                      return (
                        <div
                          key={item.id}
                          className="flex items-start space-x-2 p-2 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                        >
                          <button
                            type="button"
                            onClick={() => handleChecklistItemToggle(item.id)}
                            className={`
                              flex-shrink-0 flex items-center justify-center w-5 h-5 rounded border-2 transition-all mt-0.5
                              ${
                                isChecked
                                  ? "bg-indigo-600 dark:bg-indigo-500 border-indigo-600 dark:border-indigo-500"
                                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-indigo-500 dark:hover:border-indigo-400"
                              }
                            `}
                            title={isChecked ? "Mark as incomplete" : "Mark as complete"}
                          >
                            {isChecked && (
                              <CheckIcon className="h-3 w-3 text-white" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            {item.link ? (
                              <Link
                                to={item.link}
                                onClick={(e) => e.stopPropagation()}
                                className={`
                                  text-xs block
                                  ${
                                    isChecked
                                      ? "text-gray-500 dark:text-gray-400 line-through"
                                      : "text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400"
                                  }
                                `}
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <span
                                className={`
                                  text-xs block
                                  ${
                                    isChecked
                                      ? "text-gray-500 dark:text-gray-400 line-through"
                                      : "text-gray-900 dark:text-gray-100"
                                  }
                                `}
                              >
                                {item.label}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {allChecklistCompleted && !checklistLoading && (
                  <div className="mt-3 pt-3 border-t border-indigo-200 dark:border-indigo-800">
                    <p className="text-xs text-green-700 dark:text-green-400 font-medium text-center">
                      🎉 All setup complete!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notifications list - scrollable area */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="p-4">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center">
                <BellIcon className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No notifications
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map((notification) => {
                  // Special handling for missed appointment notifications
                  if (notification.type === 'missed_appointment' && !notification.is_read) {
                    const appointments = notification.data?.appointments || [];
                    const unrespondedAppointments = appointments.filter((apt: any) => !apt.responded);
                    
                    // Don't render if all appointments have been responded to
                    if (unrespondedAppointments.length === 0) {
                      return null;
                    }
                    
                    return (
                      <div
                        key={notification.id}
                        className="p-4 bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500"
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedNotificationIds.has(notification.id)}
                            onChange={() => toggleSelectNotification(notification.id)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                                {notification.title}
                              </h4>
                              <div className="ml-2 w-2 h-2 bg-orange-600 rounded-full"></div>
                            </div>
                            <p className="mt-1 text-sm text-orange-700 dark:text-orange-200">
                              {notification.message}
                            </p>
                          </div>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
                            title="Dismiss"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>

                        {unrespondedAppointments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {/* Bulk action buttons - commented out for now
                            <div className="flex items-center justify-end mb-2">
                              <div className="flex flex-col items-end gap-1">
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  Did they show up?
                                </span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={async () => {
                                      // Always apply to all appointments
                                      await handleBulkMissedAppointmentResponse(
                                        notification.id,
                                        unrespondedAppointments,
                                        true
                                      );
                                    }}
                                    className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition"
                                  >
                                    Yes (Showed Up)
                                  </button>
                                  <button
                                    onClick={async () => {
                                      // Always apply to all appointments
                                      await handleBulkMissedAppointmentResponse(
                                        notification.id,
                                        unrespondedAppointments,
                                        false
                                      );
                                    }}
                                    className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition"
                                  >
                                    No (Send SMS)
                                  </button>
                                </div>
                              </div>
                            </div>
                            */}

                            {unrespondedAppointments.map((appointment: any) => (
                              <div
                                key={appointment.appointment_id}
                                className="bg-white dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="mb-1">
                                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                                        {appointment.lead_name}
                                      </span>
                                    </div>
                                    {appointment.lead_phone && (
                                      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                        <PhoneIcon className="h-3 w-3" />
                                        <span>{formatPhoneNumber(appointment.lead_phone)}</span>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {appointment.formatted_datetime || `${appointment.appointment_date} at ${appointment.appointment_time}`}
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1 ml-2">
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                      Showed up?
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={async () => {
                                          await handleMissedAppointmentResponse(
                                            notification.id,
                                            appointment.appointment_id,
                                            appointment.lead_id,
                                            true,
                                            false
                                          );
                                        }}
                                        className="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition"
                                        title="Lead showed up - mark as completed"
                                      >
                                        Yes
                                      </button>
                                      <button
                                        onClick={async () => {
                                          // Automatically send SMS if they have a phone number
                                          const sendSms = appointment.lead_phone ? true : false;
                                          
                                          await handleMissedAppointmentResponse(
                                            notification.id,
                                            appointment.appointment_id,
                                            appointment.lead_id,
                                            false,
                                            sendSms
                                          );
                                        }}
                                        className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition"
                                        title="Lead didn't show up - mark as no-show and send follow-up SMS"
                                      >
                                        No
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )                        )}
                      </div>
                    )}
                          </div>
                        </div>
                      </div>
                    );
              }

                  // Low inventory notification
                  if (notification.type === 'low_inventory') {
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          !notification.is_read ? 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500' : 'border-l-4 border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedNotificationIds.has(notification.id)}
                            onChange={() => toggleSelectNotification(notification.id)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <CubeIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                              <h4 className={`text-sm font-medium ${
                                notification.is_read ? 'text-gray-900 dark:text-white' : 'text-amber-900 dark:text-amber-100'
                              }`}>
                                {notification.title}
                              </h4>
                              {!notification.is_read && (
                                <div className="w-2 h-2 bg-amber-600 rounded-full flex-shrink-0" />
                              )}
                            </div>
                            <p className={`mt-1 text-sm ${
                              notification.is_read ? 'text-gray-600 dark:text-gray-400' : 'text-amber-700 dark:text-amber-200'
                            }`}>
                              {notification.message}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {formatLastActivity(notification.created_at)}
                            </p>
                            <Link
                              to="/app/store"
                              className="mt-2 inline-flex items-center text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200"
                            >
                              View Store →
                            </Link>
                          </div>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 ml-2"
                            title="Dismiss"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Curriculum completed notification
                  if (notification.type === 'curriculum_completed') {
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          !notification.is_read ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' : 'border-l-4 border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedNotificationIds.has(notification.id)}
                            onChange={() => toggleSelectNotification(notification.id)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <AcademicCapIcon className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                              <h4 className={`text-sm font-medium ${
                                notification.is_read ? 'text-gray-900 dark:text-white' : 'text-green-900 dark:text-green-100'
                              }`}>
                                {notification.title}
                              </h4>
                              {!notification.is_read && (
                                <div className="w-2 h-2 bg-green-600 rounded-full flex-shrink-0" />
                              )}
                            </div>
                            <p className={`mt-1 text-sm ${
                              notification.is_read ? 'text-gray-600 dark:text-gray-400' : 'text-green-700 dark:text-green-200'
                            }`}>
                              {notification.message}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {formatLastActivity(notification.created_at)}
                            </p>
                            <Link
                              to="/app/students"
                              className="mt-2 inline-flex items-center text-xs font-medium text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200"
                            >
                              View Students →
                            </Link>
                          </div>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 ml-2"
                            title="Dismiss"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Regular notification display
                  return (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        !notification.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedNotificationIds.has(notification.id)}
                          onChange={() => toggleSelectNotification(notification.id)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <h4 className={`text-sm font-medium ${
                              notification.is_read
                                ? 'text-gray-900 dark:text-white'
                                : 'text-blue-900 dark:text-blue-100'
                            }`}>
                              {notification.title}
                            </h4>
                            {!notification.is_read && (
                              <div className="ml-2 w-2 h-2 bg-blue-600 rounded-full"></div>
                            )}
                          </div>
                          <p className={`mt-1 text-sm ${
                            notification.is_read
                              ? 'text-gray-600 dark:text-gray-400'
                              : 'text-blue-700 dark:text-blue-200'
                          }`}>
                            {formatNotificationMessage(notification.message)}
                          </p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {formatLastActivity(notification.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Dismiss"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
      
      {/* Dismiss Checklist Confirmation Modal */}
      {showDismissConfirm && (
        <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Dismiss Checklist?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to dismiss the onboarding checklist? You can always re-enable it later if needed.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDismissChecklist}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDismissChecklist}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationsUIContext.Provider>
  );
}
